import classNames from 'classnames'
import { Check } from 'lucide-react'

import { FOCUS_RING } from './ui'
import { STEPS } from './wizard'

/**
 * Numbered step rail. Steps up to the furthest one reached are clickable;
 * steps not reached yet are inert.
 */
export const StepRail = ({
  current,
  maxReached,
  complete,
  onSelect,
}: {
  current: number
  maxReached: number
  /** Per-step completeness (same order as STEPS). */
  complete: boolean[]
  onSelect: (index: number) => void
}) => (
  <nav aria-label="Create tournament steps" className="px-4 sm:px-6 pt-4 pb-3">
    <ol className="flex items-center gap-1 sm:gap-2">
      {STEPS.map((step, index) => {
        const isCurrent = index === current
        const reachable = index <= maxReached
        const done = complete[index] && index !== current && reachable
        return (
          <li key={step.id} className={classNames('flex min-w-0 items-center', isCurrent ? 'flex-shrink-0' : 'flex-1 sm:flex-initial lg:flex-shrink-0')}>
            <button
              type="button"
              onClick={() => reachable && onSelect(index)}
              disabled={!reachable}
              aria-current={isCurrent ? 'step' : undefined}
              aria-label={`Step ${index + 1}: ${step.label}${done ? ' (done)' : ''}`}
              className={classNames(
                'group flex min-w-0 items-center gap-2 rounded-full py-1 pl-1 pr-1 lg:pr-3 transition-colors',
                FOCUS_RING,
                reachable ? 'cursor-pointer' : 'cursor-default',
                isCurrent && 'pr-3 bg-btn-fancy-primary-end/10',
                !isCurrent && reachable && 'hover:bg-light-gray-light dark:hover:bg-dark-bg-secondary',
              )}
            >
              <span
                aria-hidden
                className={classNames(
                  'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full font-display text-sm font-bold tabular-nums',
                  isCurrent
                    ? 'bg-gradient-to-br from-btn-fancy-primary-start to-btn-fancy-primary-end text-black'
                    : done
                      ? 'bg-green/15 text-green'
                      : reachable
                        ? 'border border-dark-gray-primary text-light-typo-primary dark:text-dark-typo-primary'
                        : 'border border-light-border-primary dark:border-dark-border-primary text-light-typo-gray dark:text-dark-typo-gray',
                )}
              >
                {done ? <Check size={14} strokeWidth={3} /> : index + 1}
              </span>
              <span
                className={classNames(
                  'truncate text-[13px] font-semibold',
                  isCurrent ? 'inline text-light-typo-primary dark:text-dark-typo-primary' : '[display:none] lg:inline',
                  !isCurrent && (reachable ? 'text-light-typo-primary dark:text-dark-typo-primary' : 'text-light-typo-gray dark:text-dark-typo-gray'),
                )}
              >
                {step.label}
              </span>
            </button>
            {index < STEPS.length - 1 && (
              <span
                aria-hidden
                className={classNames(
                  'mx-1 h-px flex-1 sm:w-6 sm:flex-none lg:w-5 xl:w-8',
                  index < maxReached ? 'bg-green/40' : 'bg-light-border-primary dark:bg-dark-border-primary',
                )}
              />
            )}
          </li>
        )
      })}
    </ol>
  </nav>
)
